"use client"

import { useMemo, useState } from "react"
import { X, Search, AlertCircle, Phone, MessageSquare, Clock, Bot, User } from "@/lib/icons"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { normalizeLeadStage } from "@/lib/lead-stages"
import { useEscapeKey } from "@/hooks/useEscapeKey"

const STAGE_LABEL = {
    nuevo: "Nuevo",
    contactado: "Contactado",
    interesado: "Interesado",
    cita_solicitada: "Cita solicitada",
    cita: "Cita agendada",
}

function daysSince(dateLike) {
    if (!dateLike) return 999
    const d = new Date(dateLike)
    if (Number.isNaN(d.getTime())) return 999
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24))
}

function formatLastSeen(d) {
    const days = daysSince(d)
    if (days >= 30) return `${Math.floor(days / 30)} mes${Math.floor(days / 30) > 1 ? "es" : ""}`
    if (days >= 1) return `${days} día${days > 1 ? "s" : ""}`
    const hours = Math.floor((Date.now() - new Date(d).getTime()) / (1000 * 60 * 60))
    if (hours >= 1) return `${hours}h`
    const mins = Math.floor((Date.now() - new Date(d).getTime()) / (1000 * 60))
    return `${Math.max(1, mins)}m`
}

export function SinAtenderModal({ open, onClose, leads = [], onViewDetails }) {
    const [searchTerm, setSearchTerm] = useState("")

    useEscapeKey(open, onClose)

    const sorted = useMemo(() => {
        const items = [...leads]
        items.sort((a, b) => {
            const ta = new Date(a.ultima_interaccion || a.updated_at || a.created_at).getTime()
            const tb = new Date(b.ultima_interaccion || b.updated_at || b.created_at).getTime()
            return ta - tb
        })
        if (!searchTerm.trim()) return items
        const term = searchTerm.toLowerCase()
        return items.filter(l =>
            (l.nombre_tutor || "").toLowerCase().includes(term) ||
            (l.telefono || "").includes(term) ||
            (l.nivel_educativo || "").toLowerCase().includes(term) ||
            (l.email || "").toLowerCase().includes(term)
        )
    }, [leads, searchTerm])

    if (!open) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:p-6 pb-20">
            <div
                className="relative w-full max-w-4xl rounded-2xl bg-white shadow-2xl dark:bg-slate-900 border border-slate-200 dark:border-slate-800"
                role="dialog"
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-slate-100 p-4 sm:p-5 dark:border-slate-800 rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-400">
                            <AlertCircle className="h-5 w-5" />
                        </div>
                        <div>
                            <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">Sin atender</h2>
                            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                                {leads.length} prospecto{leads.length !== 1 ? "s" : ""} esperando respuesta hace 3+ días
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                        aria-label="Cerrar"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Search */}
                <div className="p-4 sm:p-5 pb-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Buscar por nombre, teléfono, nivel..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9"
                            autoFocus
                        />
                    </div>
                </div>

                {/* List */}
                <div className="max-h-[60vh] overflow-y-auto p-4 sm:p-5 pt-2 space-y-2">
                    {sorted.length === 0 ? (
                        <div className="text-center py-12 text-slate-400">
                            <AlertCircle className="h-10 w-10 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">
                                {leads.length === 0 ? "¡Todo al día! No hay prospectos sin atender." : "Sin resultados para la búsqueda."}
                            </p>
                        </div>
                    ) : (
                        sorted.map((lead) => {
                            const stage = normalizeLeadStage(lead.estado_lead || "")
                            const tiempo = formatLastSeen(lead.ultima_interaccion || lead.updated_at || lead.created_at)
                            const isHuman = lead.intervencion_humana === true
                            return (
                                <button
                                    key={lead.id}
                                    type="button"
                                    onClick={() => { onViewDetails?.(lead); onClose() }}
                                    className="w-full text-left flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 hover:bg-white dark:hover:bg-slate-800 px-3 py-2.5 transition-all hover:shadow-md hover:-translate-y-0.5"
                                >
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-400 flex-shrink-0">
                                        {isHuman ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-semibold text-sm text-slate-900 dark:text-white truncate">
                                                {lead.nombre_tutor || "Sin nombre"}
                                            </p>
                                            {lead.nivel_educativo && (
                                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                                    {lead.nivel_educativo}
                                                </span>
                                            )}
                                            {STAGE_LABEL[stage] && (
                                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                                                    {STAGE_LABEL[stage]}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                            {lead.telefono && (
                                                <span className="flex items-center gap-1">
                                                    <Phone className="h-3 w-3" />
                                                    {lead.telefono}
                                                </span>
                                            )}
                                            <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400 font-semibold">
                                                <Clock className="h-3 w-3" />
                                                {tiempo} sin respuesta
                                            </span>
                                        </div>
                                    </div>
                                    <Button size="sm" variant="ghost" className="flex-shrink-0 pointer-events-none">
                                        <MessageSquare className="h-3.5 w-3.5 mr-1" />
                                        Abrir
                                    </Button>
                                </button>
                            )
                        })
                    )}
                </div>
            </div>
        </div>
    )
}
